import React, { useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition, Popover } from '@headlessui/react';
import {
  BellIcon,
  UserCircleIcon,
  HomeIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.data?.auction_id) {
      navigate(`/auctions/${notification.data.auction_id}`);
    } else if (notification.data?.transaction_id) {
      navigate('/payments');
    }
  };

  return (
    <nav className="bg-[#084f3c] shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="Salvage Portal" className="h-[150px] w-[150px]" />
            <span className="font-bold text-xl text-white">Salvages Management</span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-white hover:text-gray-200 transition">
              Home
            </Link>
            <Link to="/auctions" className="text-white hover:text-gray-200 transition">
              Auctions
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/dashboard" className="text-white hover:text-gray-200 transition">
                  Dashboard
                </Link>
                {/* Direct Payments Link in Main Navigation */}
                <Link 
                  to="/payments" 
                  className="text-white hover:text-gray-200 transition flex items-center"
                >
                  <CreditCardIcon className="h-5 w-5 mr-1" />
                  Payments
                </Link>
              </>
            )}
            {isAdmin && (
              <Link to="/admin" className="text-white hover:text-gray-200 transition">
                Admin
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Notifications */}
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className="relative p-2 rounded-full hover:bg-[#0f5a38] focus:outline-none"
                      >
                        <BellIcon className="h-6 w-6 text-white" />
                        {unreadCount > 0 && (
                          <span className="absolute top-0 right-0 transform translate-x-1 -translate-y-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl overflow-hidden z-50">
                          <div className="p-4 border-b">
                            <div className="flex justify-between items-center">
                              <h3 className="font-semibold">Notifications</h3>
                              {unreadCount > 0 && (
                                <button
                                  onClick={markAllAsRead}
                                  className="text-sm text-[#084f3c] hover:text-[#0f5a38]"
                                >
                                  Mark all as read
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            {notifications.length === 0 ? (
                              <div className="p-4 text-center text-gray-500">
                                No notifications
                              </div>
                            ) : (
                              notifications.map((notification) => (
                                <motion.div
                                  key={notification.id}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className={`p-4 border-b hover:bg-gray-50 cursor-pointer ${
                                    !notification.is_read ? 'bg-blue-50' : ''
                                  }`}
                                  onClick={() => handleNotificationClick(notification)}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0">
                                      <div className="h-8 w-8 rounded-full bg-[#084f3c] bg-opacity-10 flex items-center justify-center">
                                        <span className="text-[#084f3c] text-sm">
                                          {notification.type === 'outbid' && '🔔'}
                                          {notification.type === 'bid_received' && '💰'}
                                          {notification.type === 'auction_won' && '🏆'}
                                          {notification.type === 'payment_received' && '💳'}
                                          {notification.type === 'refund_processed' && '↩️'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-gray-500 truncate">
                                        {notification.message}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        {new Date(notification.created_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                </motion.div>
                              ))
                            )}
                          </div>
                          {/* Quick link to notifications */}
<div className="p-3 bg-gray-50 border-t">
  <Link 
    to="/notifications" 
    className="block text-center text-sm text-[#084f3c] hover:text-[#0f5a38] font-medium"
  >
    View All Notifications →
  </Link>
</div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>

                {/* User Menu */}
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-[#0f5a38] focus:outline-none">
                    <div className="h-8 w-8 rounded-full bg-white text-[#084f3c] flex items-center justify-center font-bold">
                      {user?.first_name?.[0] || user?.username?.[0] || 'U'}
                    </div>
                    <span className="hidden md:block text-sm font-medium text-white">
                      {user?.first_name || user?.username}
                    </span>
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/dashboard"
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } block px-4 py-2 text-sm text-gray-700`}
                          >
                            Dashboard
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } block px-4 py-2 text-sm text-gray-700`}
                          >
                            Profile
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/payments"
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } block px-4 py-2 text-sm text-gray-700 flex items-center`}
                          >
                            <CreditCardIcon className="h-4 w-4 mr-2 text-gray-500" />
                            Payments
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } block w-full text-left px-4 py-2 text-sm text-red-600 flex items-center`}
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                            Logout
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-white hover:text-gray-200 transition"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-white text-[#084f3c] px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;